
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  fetchAccountPlans,
  fetchOrganizationAccountPlans,
  fetchAccountPlanById,
  createAccountPlan,
  updateAccountPlan,
  deleteAccountPlan,
  getCurrentQuarterPlans,
  generateQuarterlyPlans,
  getAccountPlanningStats
} from "@/services";

export function useAccountPlanning() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Query for all account plans
  const accountPlansQuery = useQuery({
    queryKey: ['accountPlans'],
    queryFn: fetchAccountPlans
  });
  
  return {
    accountPlansQuery,
  };
}

// Separate hooks for specific queries
export function useOrganizationAccountPlans(organizationId: string) {
  return useQuery({
    queryKey: ['accountPlans', 'organization', organizationId],
    queryFn: () => fetchOrganizationAccountPlans(organizationId),
    enabled: !!organizationId
  });
}

export function useAccountPlan(id: string) {
  return useQuery({
    queryKey: ['accountPlans', id],
    queryFn: () => fetchAccountPlanById(id),
    enabled: !!id
  });
}

export function useCurrentQuarterPlans(year?: number, quarter?: number) {
  return useQuery({
    queryKey: ['accountPlans', 'quarter', year || 'current', quarter || 'current'],
    queryFn: () => getCurrentQuarterPlans(year, quarter)
  });
}

export function useAccountPlanningStats() {
  return useQuery({
    queryKey: ['accountPlanningStats'],
    queryFn: getAccountPlanningStats
  });
}

// Updated useAccountPlanning hook with mutations
export function useAccountPlanningMutations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Mutation for creating an account plan
  const createAccountPlanMutation = useMutation({
    mutationFn: createAccountPlan,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['accountPlans'] });
      if (data.organization_id) {
        queryClient.invalidateQueries({ 
          queryKey: ['accountPlans', 'organization', data.organization_id] 
        });
      }
      toast({
        title: "Account plan created",
        description: "The account plan has been created successfully."
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create account plan",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  
  // Mutation for updating an account plan
  const updateAccountPlanMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateAccountPlan(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['accountPlans'] });
      queryClient.invalidateQueries({ queryKey: ['accountPlans', data.id] });
      if (data.organization_id) {
        queryClient.invalidateQueries({ 
          queryKey: ['accountPlans', 'organization', data.organization_id] 
        });
      }
      toast({
        title: "Account plan updated",
        description: "The account plan has been updated successfully."
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update account plan",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  
  // Mutation for deleting an account plan
  const deleteAccountPlanMutation = useMutation({
    mutationFn: deleteAccountPlan,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['accountPlans'] });
      toast({
        title: "Account plan deleted",
        description: "The account plan has been deleted successfully."
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete account plan",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  
  // Mutation for generating quarterly plans
  const generateQuarterlyPlansMutation = useMutation({
    mutationFn: generateQuarterlyPlans,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['accountPlans'] });
      toast({
        title: "Quarterly plans generated",
        description: `Successfully generated ${data.count} quarterly plans.`
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to generate quarterly plans",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  
  return {
    createAccountPlan: createAccountPlanMutation.mutate,
    updateAccountPlan: updateAccountPlanMutation.mutate,
    deleteAccountPlan: deleteAccountPlanMutation.mutate,
    generateQuarterlyPlans: generateQuarterlyPlansMutation.mutate,
    isCreating: createAccountPlanMutation.isPending,
    isUpdating: updateAccountPlanMutation.isPending,
    isDeleting: deleteAccountPlanMutation.isPending,
    isGenerating: generateQuarterlyPlansMutation.isPending,
  };
}
